import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { TierListItem } from "../../models/TierList";
import { getCareerPool, searchTeamSeasons } from "../../services/TierListService";
import { searchPlayers, PlayerSearchResult } from "../../services/AnalysisService";

interface Subject {
    steamId: string;
    name: string;
}

interface Props {
    mode: "teams" | "players";
    existingIds: Set<string>;
    /** Adds the cards that are not on the board yet and returns how many that was. */
    onAdd: (items: TierListItem[]) => number;
    me: Subject | null;
    notice: string;
    setNotice: (notice: string) => void;
}

interface TeamGroup {
    name: string;
    logo?: string;
    items: TierListItem[];
}

/** Debounced search that drops answers to queries the user has already typed past. */
function useSearch<T>(query: string, search: (q: string) => Promise<T[]>): T[] {
    const [results, setResults] = useState<T[]>([]);
    useEffect(() => {
        const q = query.trim();
        if (q.length < 2) {
            setResults([]);
            return;
        }
        let cancelled = false;
        const timer = setTimeout(() => {
            search(q)
                .then(r => { if (!cancelled) setResults(r); })
                .catch(() => { if (!cancelled) setResults([]); });
        }, 250);
        return () => { cancelled = true; clearTimeout(timer); };
    }, [query, search]);
    return results;
}

const TierListPoolBuilder: React.FC<Props> = ({ mode, existingIds, onAdd, me, notice, setNotice }) => {
    const { t } = useTranslation(["pages"]);
    const [query, setQuery] = useState("");
    const [careerQuery, setCareerQuery] = useState("");
    const [busy, setBusy] = useState(false);

    const teamResults = useSearch(mode === "teams" ? query : "", searchTeamSeasons);
    const playerResults = useSearch(mode === "players" ? query : "", searchPlayers);
    const careerResults = useSearch(careerQuery, searchPlayers);

    useEffect(() => {
        setQuery("");
        setCareerQuery("");
    }, [mode]);

    // One row per team name, so a team that re-registered under a new id still lists every season.
    const teamGroups = useMemo((): TeamGroup[] => {
        const byName = new Map<string, TeamGroup>();
        teamResults.forEach(item => {
            const key = item.name.trim().toLowerCase();
            const group = byName.get(key) ?? { name: item.name, logo: item.logo, items: [] };
            group.items.push(item);
            byName.set(key, group);
        });
        const q = query.trim().toLowerCase();
        const closeness = (g: TeamGroup) => {
            const name = g.name.toLowerCase();
            return name === q ? 0 : name.startsWith(q) ? 1 : 2;
        };
        const latest = (g: TeamGroup) => Math.max(...g.items.map(i => i.season ?? 0));
        return Array.from(byName.values())
            .map(g => ({ ...g, items: [...g.items].sort((a, b) => (a.season ?? 0) - (b.season ?? 0)) }))
            .sort((a, b) => closeness(a) - closeness(b) || latest(b) - latest(a))
            .slice(0, 8);
    }, [teamResults, query]);

    const add = (items: TierListItem[]) => {
        const added = onAdd(items);
        setNotice(added > 0
            ? t("pages:tierlist.addedCount", { count: added })
            : t("pages:tierlist.nothingNew"));
    };

    const addCareer = async (subject: Subject) => {
        setBusy(true);
        try {
            const items = await getCareerPool(subject.steamId, mode);
            if (items.length === 0) {
                setNotice(t("pages:tierlist.noCareer", { name: subject.name }));
            } else {
                add(items);
            }
            setCareerQuery("");
        } catch {
            setNotice(t("pages:tierlist.noCareer", { name: subject.name }));
        } finally {
            setBusy(false);
        }
    };

    const addPlayer = (p: PlayerSearchResult) => add([{ id: p.steamId, name: p.playerName }]);

    return (
        <div className="pool-builder">
            <div className="pool-builder-section">
                <input
                    type="text"
                    className="pool-builder-input"
                    placeholder={mode === "teams" ? t("pages:tierlist.searchTeams") : t("pages:tierlist.searchPlayers")}
                    value={query}
                    onChange={e => setQuery(e.target.value)}
                />
                {mode === "teams" && teamGroups.length > 0 && (
                    <div className="pool-builder-results">
                        {teamGroups.map(group => {
                            const missing = group.items.filter(i => !existingIds.has(i.id));
                            return (
                                <div key={group.name} className="pool-builder-group">
                                    <div className="pool-builder-group-head">
                                        {group.logo && <img src={group.logo} alt="" className="pool-builder-logo" />}
                                        <span className="pool-builder-name">{group.name}</span>
                                        <button
                                            className="pool-builder-btn"
                                            disabled={missing.length === 0}
                                            onClick={() => add(group.items)}
                                        >
                                            {t("pages:tierlist.addAll")} ({group.items.length})
                                        </button>
                                    </div>
                                    <div className="pool-builder-chips">
                                        {group.items.map(item => {
                                            const added = existingIds.has(item.id);
                                            return (
                                                <button
                                                    key={item.id}
                                                    className={`pool-builder-chip ${added ? "added" : ""}`}
                                                    disabled={added}
                                                    onClick={() => add([item])}
                                                >
                                                    S{item.season}
                                                </button>
                                            );
                                        })}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}
                {mode === "players" && playerResults.length > 0 && (
                    <div className="pool-builder-results">
                        {playerResults.map(p => {
                            const added = existingIds.has(p.steamId);
                            return (
                                <div key={p.steamId} className="pool-builder-row">
                                    <span className="pool-builder-name">{p.playerName}</span>
                                    <button className="pool-builder-btn" disabled={added} onClick={() => addPlayer(p)}>
                                        {added ? t("pages:tierlist.added") : t("pages:tierlist.add")}
                                    </button>
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>

            <div className="pool-builder-section pool-builder-career">
                <span className="pool-builder-label">{t("pages:tierlist.career")}</span>
                {me && (
                    <button className="pool-builder-btn primary" disabled={busy} onClick={() => addCareer(me)}>
                        {mode === "teams" ? t("pages:tierlist.myOldTeams") : t("pages:tierlist.myTeammates")}
                    </button>
                )}
                <input
                    type="text"
                    className="pool-builder-input small"
                    placeholder={mode === "teams" ? t("pages:tierlist.otherPlayersTeams") : t("pages:tierlist.otherPlayersTeammates")}
                    value={careerQuery}
                    onChange={e => setCareerQuery(e.target.value)}
                />
                {careerResults.length > 0 && (
                    <div className="pool-builder-results pool-builder-player-pick">
                        {careerResults.map(p => (
                            <button
                                key={p.steamId}
                                className="pool-builder-chip"
                                disabled={busy}
                                onClick={() => addCareer({ steamId: p.steamId, name: p.playerName })}
                            >
                                {p.playerName}
                            </button>
                        ))}
                    </div>
                )}
            </div>

            {notice && <div className="pool-builder-notice">{notice}</div>}
        </div>
    );
};

export default TierListPoolBuilder;
